/**
 * Provides an enhanced "Route" component that first verifies if the user is authorized to use the
 * application. If not, it saves the user's desired location and redirects the user to login page.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Route, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import isRequiredIf from 'react-proptype-conditional-require';
import { has } from 'lodash';

import {
  isAuthorizedSelector,
  isTokenExpiredSelector,
} from '../redux/selectors';

import { saveLoginRedirectFrom } from '../utils/redirects';

export function AuthorizedRoute({
  component,
  isAuthorized,
  isTokenExpired,
  render,
  ...rest
}) {
  return (
    <Route
      {...rest}
      render={(componentProps) => {
        // Portunus authorization lasts 24hrs, but tokens expire in 1hr.
        // In the event of an expired token (which will happen if the user
        // is idle for > 1hr), redirect the user to the login page to refresh.
        if (isTokenExpired) {
          saveLoginRedirectFrom(componentProps.location);
          return (
            <Redirect to="/logout" />
          );
        }
        if (isAuthorized) {
          if (component) {
            return React.createElement(component, { ...rest, ...componentProps });
          }
          if (render) {
            return render({ ...rest, ...componentProps });
          }
          return (
            <Redirect
              to={{
                pathname: '/404-not-found',
                state: { from: componentProps.location },
              }}
            />
          );
        }

        // The current path must be saved in localstorage to persist across
        // the redirects to the SSO provider. History state is saved in memory
        // only and will be cleared.
        saveLoginRedirectFrom(componentProps.location);
        return (
          <Redirect
            to={{
              pathname: '/login',
            }}
          />
        );
      }}
    />
  );
}

AuthorizedRoute.propTypes = {
  // A `component` prop or a `render` prop is required. Both should not be supplied.
  // If both are supplied, the `component` prop will take priority.
  // https://reacttraining.com/react-router/web/api/Route/render-func
  component: isRequiredIf(PropTypes.func, props => !has(props, 'render')),
  isAuthorized: PropTypes.bool.isRequired,
  isTokenExpired: PropTypes.bool.isRequired,
  render: isRequiredIf(PropTypes.func, props => !has(props, 'component')),
};

AuthorizedRoute.defaultProps = {
  component: undefined,
  render: undefined,
};

function mapStateToProps(state) {
  return {
    isAuthorized: isAuthorizedSelector(state),
    isTokenExpired: isTokenExpiredSelector(state),
  };
}

export default connect(mapStateToProps)(AuthorizedRoute);



// WEBPACK FOOTER //
// ./features/authentication/AuthorizedRoute/index.js